import React, { useEffect, useState } from "react";
import { Target, Trophy, Megaphone, TrendingUp, CheckCircle2 } from "lucide-react";
import { ProgressWave } from "@/components/ProgressWave";
import { LeaderboardTable } from "@/components/LeaderboardTable";
import { api, money } from "@/lib/api";

export default function MyCampaigns() {
  const [campaigns, setCampaigns] = useState(null);
  useEffect(() => { api.get("/volunteer/campaigns").then(({ data }) => setCampaigns(data)).catch(() => setCampaigns([])); }, []);

  if (!campaigns) {
    return <div className="grid place-items-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-4 border-[#e4d8c4] border-t-[#65949c]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="volunteer-mycampaigns">
      <div>
        <span className="hf-eyebrow">The impact of your work</span>
        <h1 className="mt-1 font-heading text-3xl font-semibold text-[#00334a]">My Campaigns</h1>
        <p className="mt-1 text-sm text-[#7c7a6a]">Read-only view of the fundraising your projects support.</p>
      </div>

      {campaigns.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#e4d8c4] bg-white p-12 text-center">
          <Megaphone className="mx-auto h-10 w-10 text-[#e4d8c4]" />
          <p className="mt-3 font-semibold text-[#00334a]">No connected campaigns yet</p>
          <p className="text-sm text-[#7c7a6a]">When your project is linked to a campaign, it appears here.</p>
        </div>
      ) : campaigns.map((c) => (
        <div key={c.id} className="overflow-hidden rounded-3xl border border-[#e4d8c4] bg-white" data-testid={`mycampaign-${c.id}`}>
          <div className="hf-gradient-deep p-6 text-white">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="font-heading text-2xl font-semibold">{c.name}</h2>
                {c.description && <p className="mt-1 max-w-2xl text-sm text-sky-100/90">{c.description}</p>}
              </div>
              <div className="text-right">
                <div className="font-heading text-3xl font-bold text-[#e7b576]">{money(c.raised)}</div>
                <div className="text-xs text-white/70">of {money(c.goal)} goal</div>
              </div>
            </div>
            <div className="mt-4"><ProgressWave pct={c.pct} height="h-4" showMilestones /></div>
            <div className="mt-3 flex flex-wrap gap-2">
              {[25, 50, 75, 100].map((m) => (
                <span key={m} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${c.milestones?.[String(m)] ? "bg-[#e7b576] text-[#00334a]" : "bg-white/15 text-white/70"}`}>
                  {c.milestones?.[String(m)] && <CheckCircle2 className="h-3.5 w-3.5" />} {m}%
                </span>
              ))}
            </div>
          </div>

          <div className="grid gap-6 p-6 lg:grid-cols-2">
            <div>
              <h3 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-[#00334a]"><Trophy className="h-5 w-5 text-[#e7b576]" /> Leaderboard</h3>
              <LeaderboardTable rows={c.board || []} />
            </div>
            <div className="space-y-6">
              <div>
                <h3 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-[#00334a]"><TrendingUp className="h-5 w-5 text-[#65949c]" /> Recent gifts</h3>
                <div className="space-y-2">
                  {(c.recent_activity || []).length === 0 && <div className="rounded-xl border border-dashed border-[#e4d8c4] p-6 text-center text-sm text-[#7c7a6a]">No gifts logged yet.</div>}
                  {(c.recent_activity || []).map((d) => (
                    <div key={d.id} className="flex items-center justify-between rounded-xl border border-[#e4d8c4] bg-[#f8f3e9] px-4 py-2.5">
                      <div><div className="font-semibold text-[#00334a]">{d.donor_name || "Anonymous"}</div><div className="text-xs capitalize text-[#7c7a6a]">{d.method} · {new Date(d.created_at).toLocaleDateString()}</div></div>
                      <div className="font-heading font-bold text-[#65949c]">{money(d.amount)}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-[#00334a]"><Megaphone className="h-5 w-5 text-[#c97049]" /> Campaign updates</h3>
                <div className="space-y-2">
                  {(c.updates || []).length === 0 && <div className="rounded-xl border border-dashed border-[#e4d8c4] p-6 text-center text-sm text-[#7c7a6a]">No updates posted yet.</div>}
                  {(c.updates || []).map((u) => (
                    <div key={u.id} className="rounded-xl border border-[#e4d8c4] bg-white p-3">
                      <div className="font-semibold text-[#00334a]">{u.subject}</div>
                      <div className="mt-0.5 whitespace-pre-line text-sm text-[#5a5849]">{u.message}</div>
                      <div className="mt-1 text-xs text-[#7c7a6a]">{new Date(u.created_at).toLocaleDateString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
